// RESULTADOS-06C-UPLOAD-SECURITY-01
//
// Servicio documental de resultados. SEPARADO de `SubirMedia` (ServiceExternal.js),
// que tiene la URL de produccion hardcodeada y es compartido por otros 7
// modulos -- este servicio usa SIEMPRE `host` de Global.js (DEV -> backend DEV,
// PROD -> backend configurado), nunca un string magico.
//
// La UX documental nueva (06D) debe usar EXCLUSIVAMENTE estos metodos, nunca
// SubirMedia legacy. El modal 03B actual sigue con multimedia[]/SubirMedia
// hasta que 06D lo retire.
import axios from "axios";
import { BmHttp, host } from "../utils/Global";
import { GetToken } from "../utils/storeUsuario";

// Cabeceras multipart con el mismo token que BmHttp, resolviendo el host por config.
const uploadUrl = (path) => `${String(host).replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
const authHeaders = () => {
  const t = GetToken();
  return { Authorization: "Bearer " + (t?.token ?? "") };
};

// --- lectura ---
export const getResultadoDocumentos = async (resultadoId, includeHistory = false) => {
  try {
    const { data } = await BmHttp().get(`resultados/${resultadoId}/documentos`, {
      params: includeHistory ? { include_history: true } : {},
    });
    return data;
  } catch (error) {
    return error.response?.data ?? { success: false, data: [], message: "Error de red" };
  }
};

// --- link-only (06B, sin archivo) ---
export const createResultadoDocumentoLink = async (resultadoId, { tipo_documento_id, link_url, descripcion }) => {
  try {
    const { data } = await BmHttp().post(`resultados/${resultadoId}/documentos`, { tipo_documento_id, link_url, descripcion });
    return data;
  } catch (error) {
    return error.response?.data ?? { success: false, message: "Error de red" };
  }
};

// --- metadata (06B) ---
export const updateResultadoDocumento = async (resultadoId, documentoId, { tipo_documento_id, link_url, descripcion }) => {
  try {
    const { data } = await BmHttp().put(`resultados/${resultadoId}/documentos/${documentoId}`, { tipo_documento_id, link_url, descripcion });
    return data;
  } catch (error) {
    return error.response?.data ?? { success: false, message: "Error de red" };
  }
};

// --- quitar (06B, desactivacion logica) ---
export const deleteResultadoDocumento = async (resultadoId, documentoId) => {
  try {
    const { data } = await BmHttp().delete(`resultados/${resultadoId}/documentos/${documentoId}`);
    return data;
  } catch (error) {
    return error.response?.data ?? { success: false, message: "Error de red" };
  }
};

// --- reemplazo metadata (06B) ---
export const replaceResultadoDocumento = async (resultadoId, documentoId, payload) => {
  try {
    const { data } = await BmHttp().post(`resultados/${resultadoId}/documentos/${documentoId}/replace`, payload);
    return data;
  } catch (error) {
    return error.response?.data ?? { success: false, message: "Error de red" };
  }
};

// --- upload con archivo (06C, multipart) ---
export const uploadResultadoDocumento = async (resultadoId, { tipo_documento_id, descripcion, link_url, archivo }) => {
  try {
    const fd = new FormData();
    fd.append("tipo_documento_id", tipo_documento_id);
    if (descripcion) fd.append("descripcion", descripcion);
    if (link_url) fd.append("link_url", link_url);
    fd.append("archivo", archivo);
    const { data } = await axios.post(uploadUrl(`resultados/${resultadoId}/documentos/upload`), fd, { headers: authHeaders() });
    return data;
  } catch (error) {
    return error.response?.data ?? { success: false, message: "Error de red" };
  }
};

// --- reemplazo con archivo (06C, multipart) ---
export const replaceResultadoDocumentoUpload = async (resultadoId, documentoId, { tipo_documento_id, descripcion, link_url, archivo }) => {
  try {
    const fd = new FormData();
    if (tipo_documento_id) fd.append("tipo_documento_id", tipo_documento_id);
    if (descripcion) fd.append("descripcion", descripcion);
    if (link_url) fd.append("link_url", link_url);
    fd.append("archivo", archivo);
    const { data } = await axios.post(uploadUrl(`resultados/${resultadoId}/documentos/${documentoId}/replace-upload`), fd, { headers: authHeaders() });
    return data;
  } catch (error) {
    return error.response?.data ?? { success: false, message: "Error de red" };
  }
};

// --- descarga autenticada (06D) ---
// NUNCA navegar a documento.archivo_url (para documentos nuevos es null: root
// privado). NUNCA token en query string. Se usa BmHttp() (cliente autenticado)
// con responseType blob.
export const getResultadoDocumentoBlob = async (resultadoId, documentoId, { asAttachment = false } = {}) => {
  try {
    const res = await BmHttp().get(`resultados/${resultadoId}/documentos/${documentoId}/descargar`, {
      params: asAttachment ? { download: "1" } : {},
      responseType: "blob",
    });
    return { success: true, blob: res.data, contentType: res.headers?.["content-type"] ?? null };
  } catch (error) {
    // El backend responde JSON en error; con responseType blob hay que leerlo.
    return { success: false, message: "No se pudo obtener el documento", status: error.response?.status ?? null };
  }
};

// Descarga el archivo como adjunto (attachment) via el endpoint autenticado 06D
// y dispara la descarga en el navegador con un <a download> temporal. Nunca
// navega a archivo_url ni a /store/.
export const descargarResultadoDocumento = async (resultadoId, documentoId, { filenameHint } = {}) => {
  const r = await getResultadoDocumentoBlob(resultadoId, documentoId, { asAttachment: true });
  if (!r.success) return r;
  const ext = (r.contentType || "").includes("pdf") ? "pdf"
    : (r.contentType || "").includes("png") ? "png"
    : (r.contentType || "").includes("jpeg") ? "jpg" : "bin";
  const nombre = filenameHint || `documento-${documentoId}.${ext}`;
  const objectUrl = URL.createObjectURL(r.blob);
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = nombre;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    setTimeout(() => { try { URL.revokeObjectURL(objectUrl); } catch (e) { /* noop */ } }, 4000);
  }
  return { success: true, objectUrl, filename: nombre };
};

// Helper para el futuro Drawer (06E): abre el archivo en una pestaña nueva sin
// exponer el bearer token. Devuelve el objectURL para que el llamador lo revoque.
export const abrirResultadoDocumento = async (resultadoId, documentoId) => {
  const r = await getResultadoDocumentoBlob(resultadoId, documentoId);
  if (!r.success) return r;
  const objectUrl = URL.createObjectURL(r.blob);
  const win = window.open(objectUrl, "_blank", "noopener,noreferrer");
  // liberar cuando la pestaña ya cargó (o tras un margen si no hay referencia)
  setTimeout(() => { try { URL.revokeObjectURL(objectUrl); } catch (e) { /* noop */ } }, 60000);
  return { success: true, objectUrl, win };
};

// Export interno para tests de construccion de URL (no usar en componentes).
export const __uploadUrl = uploadUrl;
